import { uploadFileCloudinary } from "./api";

/** Max characters for product description (UI + validation). */
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 500;

/** Max characters for product title (UI + validation). */
export const PRODUCT_TITLE_MAX_LENGTH = 50;

const HTTP_URL_RE = /^https?:\/\//i;

/**
 * Remote images served from the API / CDN (skip Cloudinary re-upload).
 */
export function isRemoteImageUrl(preview) {
  return typeof preview === "string" && HTTP_URL_RE.test(preview.trim());
}

export async function uploadSingleImageToCloudinary(file, options = {}) {
  const { onProgress, log } = options;

  try {
    const response = await uploadFileCloudinary(file, onProgress);
    const url = response?.data?.secure_url;

    if (!url) {
      throw new Error("Cloudinary response missing secure_url");
    }

    log?.("debug", "Cloudinary upload OK", { fileName: file?.name, url });
    return url;
  } catch (err) {
    log?.("error", "Cloudinary upload failed", {
      fileName: file?.name,
      message: err?.message,
      response: err?.response?.data,
    });
    throw err;
  }
}

/**
 * Upload only new image files (local `File` objects). Skips existing URLs.
 * @param {Array<{ file?: File | null; isExisting?: boolean; preview?: string }>} imageItems
 * @param {{ onFileProgress?: (info: { index: number; fileName: string; progress: number }) => void; log?: (level: string, msg: string, meta?: object) => void }} [options]
 * @returns {Promise<string[]>} secure_url values in the same order as new files appear in `imageItems`
 */
export async function uploadNewProductImagesToCloudinary(imageItems, options = {}) {
  const { onFileProgress, log } = options;
  const itemsWithFiles = imageItems.filter((item) => item?.file instanceof File);

  const urls = [];
  for (let i = 0; i < itemsWithFiles.length; i++) {
    const item = itemsWithFiles[i];
    const file = item.file;
    try {
      const url = await uploadSingleImageToCloudinary(file, {
        onProgress: (progress) => {
          onFileProgress?.({ index: i, fileName: file.name, progress });
        },
        log,
      });
      urls.push(url);
    } catch (err) {
      throw err;
    }
  }
  return urls;
}

/**
 * Build final image URL list: keep existing remote URLs in order, insert uploaded URLs for new files.
 */
export function mergeProductImageUrlsAfterUpload(imageItems, uploadedUrlsInOrder) {
  let nextNewIndex = 0;
  const merged = [];

  for (const item of imageItems) {
    if (item?.isExisting && isRemoteImageUrl(item.preview)) {
      merged.push(item.preview.trim());
      continue;
    }
    if (item?.file instanceof File) {
      const url = uploadedUrlsInOrder[nextNewIndex++];
      if (!url) {
        throw new Error("Internal error: missing URL for a newly uploaded image");
      }
      merged.push(url);
      continue;
    }
    throw new Error("Invalid image row: expected either an existing URL or a new file");
  }

  if (nextNewIndex !== uploadedUrlsInOrder.length) {
    throw new Error("Internal error: uploaded URL count does not match new files");
  }

  return merged;
}

function parsePositiveNumber(raw) {
  if (raw === "" || raw === null || raw === undefined) {
    return { ok: false, value: NaN };
  }
  const n = parseFloat(String(raw).replace(/,/g, "").trim());
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, value: n };
  }
  return { ok: true, value: n };
}

/**
 * @returns {{ ok: boolean; errors: Record<string, string>; errorList: { field: string; message: string }[] }}
 */
export function validateEditProductForm({ productDetails, imageItems }) {
  const errors = {};

  const titleStr = String(productDetails?.title ?? "");
  const name = titleStr.trim();
  if (!name) {
    errors.title = "Product title is required";
  } else if (titleStr.length > PRODUCT_TITLE_MAX_LENGTH) {
    errors.title = `Product title must be at most ${PRODUCT_TITLE_MAX_LENGTH} characters`;
  }

  const descriptionStr = String(productDetails?.description ?? "");
  const descriptionTrimmed = descriptionStr.trim();
  if (!descriptionTrimmed) {
    errors.description = "Description is required";
  } else if (descriptionStr.length > PRODUCT_DESCRIPTION_MAX_LENGTH) {
    errors.description = `Description must be at most ${PRODUCT_DESCRIPTION_MAX_LENGTH} characters`;
  }

  const priceCheck = parsePositiveNumber(productDetails?.price);
  if (productDetails?.price === "" || productDetails?.price == null) {
    errors.price = "Price is required";
  } else if (!priceCheck.ok) {
    errors.price = "Enter a valid non-negative number for price";
  }

  const weightRaw = productDetails?.weight;
  if (weightRaw !== "" && weightRaw != null) {
    const weightCheck = parsePositiveNumber(weightRaw);
    if (!weightCheck.ok) {
      errors.weight = "Enter a valid non-negative number for weight";
    }
  }

  const categoryId = productDetails?.category?._id ?? productDetails?.category;
  if (!categoryId) errors.category = "Category is required";

  const countryId = productDetails?.origin?._id ?? productDetails?.origin;
  if (!countryId) errors.origin = "Product origin is required";

  if (!imageItems?.length) {
    errors.images = "Add at least one product image";
  }

  const errorList = Object.entries(errors).map(([field, message]) => ({ field, message }));
  return { ok: errorList.length === 0, errors, errorList };
}

/**
 * Build GraphQL variables for adminEditProduct.
 * Optional fields `weight` and `productState` are `null` when not provided.
 */
export function buildAdminEditProductVariables({ productId, productDetails, finalImageUrls }) {
  const price = parseFloat(String(productDetails.price).replace(/,/g, "").trim());

  const weightRaw = productDetails?.weight;
  let weight = null;
  if (weightRaw !== "" && weightRaw != null) {
    const w = parseFloat(String(weightRaw).replace(/,/g, "").trim());
    weight = Number.isFinite(w) ? w : null;
  }

  const stateId = productDetails?.state?._id ?? productDetails?.state;
  const productState =
    stateId !== "" && stateId != null ? String(stateId) : null;

  const categoryId = productDetails?.category?._id ?? productDetails?.category;
  const categories = categoryId ? [String(categoryId)] : [];

  return {
    productId: String(productId),
    country: String(productDetails?.origin?._id ?? productDetails?.origin ?? ""),
    // productState,
    name: String(productDetails?.title ?? "").trim(),
    description: String(productDetails?.description ?? "").trim(),
    isNegotiable: Boolean(productDetails?.negotiable),
    price,
    weight,
    categories,
    images: (finalImageUrls ?? []).filter(Boolean).map(String),
  };
}

export function logEditProductDebug(stage, payload) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.debug(`[EditProduct:${stage}]`, payload);
  }
}
